import order from 'data/Order/selectors'
import { FilledType, HistoricalOrderType, OrderType } from 'data/Order/types'
import { StateType } from 'data/types'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import sortBy from 'lodash/sortBy'
import { createSelector } from 'reselect'

import * as positions from './positions/selectors'
import { HistoricalStateType } from './reducers'
import { TransferType } from './types'

const getHistorical = (state: StateType): HistoricalStateType => state.historical

const getTransfers: (state: StateType) => void | Array<TransferType> = createSelector(
  getHistorical,
  ({ transfers }) => (transfers ? sortBy(transfers, ['created']) : undefined)
)

const getCanLoadMore: (state: StateType) => boolean = createSelector(
  getHistorical,
  ({ canLoadMore }) => canLoadMore
)

const getIsLoadingOrders: (state: StateType) => boolean = createSelector(
  getHistorical,
  ({ isLoadingOrders }) => isLoadingOrders
)

const getIsLoadingTrades: (state: StateType) => boolean = createSelector(
  getHistorical,
  ({ isLoadingTrades }) => isLoadingTrades
)

const getDeposits: (state: StateType) => void | Array<TransferType> = createSelector(
  getTransfers,
  (data) => (data ? data.filter(({ type }) => type === 'DEPOSIT') : undefined)
)

const getOrders: (state: StateType) => Array<HistoricalOrderType> = createSelector(
  getHistorical,
  ({ orders }) => orders ?? []
)

const getFilledOrders: (state: StateType) => Array<OrderType> = createSelector(
  getHistorical,
  ({ filledOrders }) =>
    filledOrders.sort((order1, order2) => Number(order2.sendingTime) - Number(order1.sendingTime))
)

const getFilledTrades: (state: StateType) => Array<FilledType> = createSelector(
  getHistorical,
  ({ filledTrades }) =>
    filledTrades.sort((trade1, trade2) => trade2.transactionTime - trade1.transactionTime)
)

const getOrderDetails: (state: StateType) => OrderType | undefined = createSelector(
  getHistorical,
  ({ currentOrder }) => (isNil(currentOrder) ? undefined : currentOrder)
)

// TODO remove when totalFee is coming from the backend
const getLatestFeeForOrder: (state: StateType) => number | void = createSelector(
  getOrderDetails,
  order.getLive,
  (currentOrder, orders) => {
    if (!currentOrder || isEmpty(orders)) return 0
    const order = orders.find(
      ({ ordStatus, orderID }) => ordStatus === 'partial' && orderID === currentOrder.orderID
    )
    return order ? order.fee : 0
  }
)

const getWithdrawals: (state: StateType) => void | Array<TransferType> = createSelector(
  getTransfers,
  (data) => (data ? data.filter(({ type }) => type === 'WITHDRAWAL') : undefined)
)

const getLookback = (state: StateType) => state.historical.lookback

export default {
  getCanLoadMore,
  getDeposits,
  getFilledOrders,
  getFilledTrades,
  getIsLoadingOrders,
  getIsLoadingTrades,
  getLatestFeeForOrder,
  getLookback,
  getOrderDetails,
  getOrders,
  getTransfers,
  getWithdrawals,
  positions
}
